//@ts-nocheck
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ExclamationIcon } from '../../components/icons';
import { getColorForLabel } from '../../utils/colorUtils';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { fetchMeeting } from '../../store/slices/task/taskSlice';
import { Link } from 'react-router-dom';

function ApplicationTaskMeeting() {

  const [meetingData, setMeetingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { base_slug, jobId, appId, MeetingId } = useParams();
  const dispatch = useAppDispatch();
  const userDetailsData = JSON.parse(localStorage.getItem("baseMembersDetails") || "{}");
  const baseMembers = userDetailsData?.data?.baseMembers || [];
  const [scoreCardName, setScoreCardName] = useState(null);

  // Function to find scorecard name by stage ID
  const getScorecardNameByStageId = (stageId) => {
    if (!stageId) return null;

    const baseScoreCardData = JSON.parse(localStorage.getItem("baseScoreCardDataByJob") || "{}");
    const scorecards = baseScoreCardData?.data || [];

    const scorecard = scorecards.find(item => item.stage_id === stageId);
    return scorecard ? scorecard.scorecard_name : null;
  };

  // Function to find host name by ID
  const getHostName = (hostId) => {
    if (!hostId) return { name: "Not specified", profilePic: null };

    // Check if we have organiserDetail in the meeting data
    if (meetingData?.organiserDetail && parseInt(hostId, 10) === parseInt(meetingData.organiser, 10)) {
      return {
        name: meetingData.organiserDetail.name,
        profilePic: meetingData.organiserDetail.profile_pic
      };
    }

    const parsedHostId = parseInt(hostId, 10);
    const host = baseMembers.find(member => member.id == parsedHostId);

    return host ? { name: host.name, profilePic: host.profile_pic } : { name: `Host ID: ${hostId}`, profilePic: null };
  };

  // Function to get multiple hosts names if hosts is a comma-separated string
  const getHostsNames = (hostsString) => {
    if (!hostsString) return [];

    const hostIds = hostsString.split(',').map(id => id.trim());
    return hostIds.map(id => getHostName(id));
  };

  useEffect(() => {
    const fetchMeetingDetails = async () => {
      setLoading(true);
      try {
        const res = await dispatch(
          fetchMeeting({
            typeId: MeetingId
          })
        ).unwrap();

        if (res.status === 200) {
          setMeetingData(res.data);

          // Find scorecard name if stage_id exists
          if (res.data.stage_id) {
            const foundScoreCardName = getScorecardNameByStageId(res.data.stage_id);
            if (foundScoreCardName) {
              setScoreCardName(foundScoreCardName);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching meeting details:", error);
        setError("Failed to load meeting details");
      } finally {
        setLoading(false);
      }
    };

    fetchMeetingDetails();
  }, [dispatch, MeetingId]);

  // Format time from API (HH:MM:SS) to more readable format
  const formatTime = (timeString) => {
    if (!timeString || timeString === "00:00:00") return "Not specified";

    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12; // Convert 0 to 12 for 12 AM

    return `${hour12}:${minutes} ${period}`;
  };

  // Format date (YYYY-MM-DD) to more readable format
  const formatDate = (dateString) => {
    if (!dateString) return "Not specified";

    const date = new Date(dateString);

    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
    const monthName = date.toLocaleDateString('en-US', { month: 'long' });
    const day = date.getDate();
    const year = date.getFullYear();

    return `${dayName} - ${monthName} ${day}, ${year}`;
  };

  // Format date like "25 March, 2025"
  const formatSimpleDate = (dateString) => {
    if (!dateString) return "Not specified";

    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'long' });
    const year = date.getFullYear();

    return `${day} ${month}, ${year}`;
  };

  if (loading) return <div className="flex items-center justify-center relative z-50" style={{ height: 'calc(100vh - 191px)' }}>
    <div className="simple-spinner-loader">
      <span></span>
    </div>
  </div>

  if (error) return <div className="text-center py-4 text-red-500">Error: {error}</div>;

  if (!meetingData) return <div className='w-full mt-5 flex h-[300px] justify-center items-center flex-col gap-y-3'>
    <ExclamationIcon />
    <p className='text-[#0000007a] text-[13px]'>No results found</p>
  </div>;

  // Get organizer info directly from organiserDetail when available
  const organizerInfo = meetingData.organiserDetail ?
    { name: meetingData.organiserDetail.name, profilePic: meetingData.organiserDetail.profile_pic } :
    getHostName(meetingData.organiser);


  if (!meetingData) return <div className='w-full mt-5 flex h-[300px] justify-center items-center flex-col gap-y-3'>
    <ExclamationIcon />
    <p className='text-[#0000007a] text-[13px]'>No results found</p>
  </div>;

  return (
    <div className="mt-[0px] px-4 lg:px-2 py-4 lg:py-[32px] h-[calc(100vh_-_62px)] overflow-y-auto">
      <div className="w-full max-w-[710px] mx-auto">
        <h2 className='text-[16px] lg:text-lg font-medium text-gray-900'>Meeting Details</h2>
        <div className='bg-[#e0e0e0] w-full h-[1px] mt-4 mb-[22px] lg:mb-[34px] lg:mt-7'></div>



        {/* Meeting Details Section */}
        <section className="space-y-6">
          <div className="flex items-center">
            <span className="w-32 text-sm text-[#374151] font-medium leading-4 capitalize block">Date:</span>
            <span className="font-normal text-[#374151] text-[14px] wg-antialiased leading-4 capitalize">{formatDate(meetingData.date)}</span>
          </div>
          <div className="flex items-center">
            <span className="w-32 text-sm text-[#374151] font-medium leading-4 capitalize block">Start Time:</span>
            <span className="font-normal text-[#374151] text-[14px] wg-antialiased leading-4 capitalize">
              {formatTime(meetingData.from_time)}
              {meetingData.till_time && meetingData.till_time !== "00:00:00" && (
                <span className='ml-1'> - {formatTime(meetingData.till_time)}</span>
              )}
            </span>
          </div>
          <div className="flex items-center">
            <span className="w-32 text-sm text-[#374151] font-medium leading-4 capitalize block">Timezone:</span>
            <span className="font-normal text-[#374151] text-[14px] wg-antialiased leading-4 capitalize">{meetingData.timezone || "Not specified"}</span>
          </div>

          {/* Organizer display */}
          <div className="flex items-center">
            <span className="w-32 text-sm text-[#374151] font-medium leading-4 capitalize block">Organizer:</span>
            <div className="flex items-center gap-2">
              {organizerInfo.profilePic ? (
                <img
                  src={`${process.env.REACT_APP_API_BASE_URL}/${organizerInfo.profilePic}`}
                  alt={`${organizerInfo.name || 'Profile'}`}
                  className="h-6 w-6 flex-none rounded-full ring-1 ring-gray-900/10"
                />
              ) : (
                <div style={{ backgroundColor: getColorForLabel(organizerInfo.name.charAt(0) || '') }}
                  className="w-6 h-6 rounded-full flex items-center justify-center leading-5">
                  <span className="font-medium text-white leading-6 text-sm"
                  >{organizerInfo.name.charAt(0).toUpperCase()}</span>
                </div>
              )}
              <span className="font-normal text-[#374151] text-[14px] wg-antialiased leading-4 capitalize">{organizerInfo.name}</span>
            </div>
          </div>

          {/* Hosts display with profile pics - only show if hosts exist */}
          {meetingData.hosts && meetingData.hosts.trim() !== "" && (
            <div className="flex items-start">
              <span className="w-32 text-sm text-[#374151] font-medium leading-4 block">Host(s):</span>
              <div className="flex flex-col gap-2">
                {getHostsNames(meetingData.hosts).map((host, index) => (
                  <div key={index} className="flex items-center gap-2">
                    {host.profilePic ? (
                      <img
                        src={`${process.env.REACT_APP_API_BASE_URL}/${host.profilePic}`}
                        alt={`${host.name || 'Profile'}`}
                        className="h-6 w-6 flex-none rounded-full ring-1 ring-gray-900/10"
                      />
                    ) : (
                      <div style={{ backgroundColor: getColorForLabel(host.name.charAt(0) || '') }}
                        className="w-6 h-6 rounded-full flex items-center justify-center leading-5">
                        <span className="font-medium text-white leading-6 text-sm"
                        >{host.name.charAt(0).toUpperCase()}</span>
                      </div>
                    )}
                    <span className="font-normal text-[#374151] text-[14px] wg-antialiased leading-4 capitalize">{host.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center">
            <span className="w-32 text-sm text-[#374151] font-medium leading-4 capitalize block">Location:</span>
            <span className="font-normal text-[#374151] text-[14px] wg-antialiased leading-4 capitalize">{meetingData.location || "Not specified"}</span>
          </div>

          {meetingData.location_details && (
            <div className="flex items-start">
              <span className="w-32 text-sm text-[#374151] font-medium leading-4 capitalize block">Location Details:</span>
              {
                meetingData.location === "video" &&
                <Link to={meetingData.location_details} target="_blank" className="text-blue-500 underline">
                  {meetingData.location_details}
                </Link>
              }
              {
                meetingData.location === "audio" &&
                <Link to={meetingData.location_details} target="_blank" className="text-blue-500 underline">
                  {meetingData.location_details}
                </Link>
              }
              {
                meetingData.location === "office" &&
                <span className="font-normal text-[#374151] text-[14px] wg-antialiased leading-4">
                  {meetingData.location_details}
                </span>
              }
            </div>
          )}

          <div className="flex items-center">
            <span className="w-32 text-sm text-[#374151] font-medium leading-4 capitalize block">Status:</span>
            <div className='table-tooltip flex items-center max-w-fit gap-[6px]'>
              {
                meetingData.current_state === 'done' ? (
                  <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20 capitalize">
                    {meetingData.current_state}
                  </span>
                ) : meetingData.current_state === 'pending' ? (
                  <span className="inline-flex items-center rounded-md bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-800 ring-1 ring-inset ring-yellow-600/20 capitalize">
                    {meetingData.current_state}
                  </span>
                ) : meetingData.current_state === 'close' ? (
                  <span className="inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/10 capitalize">
                    {meetingData.current_state}
                  </span>
                ) : meetingData.current_state === 'cancelled' ? (
                  <span className="inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/10 capitalize">
                    {meetingData.current_state}
                  </span>
                ) : meetingData.current_state === 'scheduled' ? (
                  <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10 capitalize">
                    {meetingData.current_state}
                  </span>
                ) : null
              }
            </div>
          </div>

          {meetingData.scorecard_name && (
            <div className="flex items-center">
              <span className="w-32 text-sm text-[#374151] font-medium leading-4 capitalize block">Scorecard:</span>
              <span className="font-normal text-[#374151] text-[14px] wg-antialiased leading-4 capitalize">{meetingData.scorecard_name}</span>
            </div>
          )}

          {scoreCardName && (
            <div className="flex items-center">
              <span className="w-32 text-sm text-[#374151] font-medium leading-4 capitalize block">Stage Id:</span>
              <span className="font-normal text-[#374151] text-[14px] wg-antialiased leading-4 capitalize">{scoreCardName}</span>
            </div>
          )}

          {meetingData.created_at && (
            <div className="flex items-center">
              <span className="w-32 text-sm text-[#374151] font-medium leading-4 capitalize block">Created at:</span>
              <span className="font-normal text-[#374151] text-[14px] wg-antialiased leading-4 capitalize">
                {formatSimpleDate(meetingData.created_at)}
                {meetingData.updated_at && meetingData.updated_at !== meetingData.created_at && (
                  <span> - {formatSimpleDate(meetingData.updated_at)}</span>
                )}
              </span>
            </div>
          )}
        </section>

        {/* Footer with details */}
        {meetingData.details && (
          <footer className="mt-6 border-t">
            <div className='my-5'>
              <p className="font-normal text-[#374151] text-[14px] wg-antialiased leading-4 capitalize">{meetingData.details}</p>
            </div>
          </footer>
        )}



      </div>
    </div>
  );
}

export default ApplicationTaskMeeting;